import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, IconButton,
    Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import type { UnmatchedColumn } from '@/services/accountService/glInvoiceService';
import { confirmLegacyMapping, fetchColumnDefs } from '@/services/accountService/glInvoiceService';
import { fetchLeafGlCategories } from '@/services/accountService/glCategoryService';
import type { GlCategory } from '@/services/accountService/glCategoryService';
import { checkAliasExists } from '@/services/accountService/glInvoiceService';
import { listAccountsByCompany } from '@/services/accountService/companyGLCategoryService';
import { listAll as listAllFsCategories } from '@/services/accountService/fsCategoryService';
import type { FsCategory } from '@/services/accountService/fsCategoryService';
import { InputField } from '@/pages/components/common/InputField';
import {
    useGlInvoiceCreateCtx,
    createStartPreload,
    remapPreloadedData,
    remapColumnMetaMap,
} from '@/pages/components/account/gl-invoice/create-stepper/context';
import {
    applyCompanyAliasToMappingRows,
    buildAliasTitleToCodeMap,
    buildMappingConfirmSummary,
    canApplySuggestionChip,
    countGlSheetRowsWithAmount,
    filterGlCategoriesForHeader,
    shouldAutoSelectSuggestion,
    sortGlOptionsWithSuggestion,
} from '@/utils/glColumnMappingHelpers';
import { isExHkdColumnName } from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import MappingAddGlCategoryDialog, {
    type AddGlCategoryPrefill,
} from '@/pages/components/account/gl-invoice/create-stepper/MappingAddGlCategoryDialog';

// ===================== Types =====================

export interface MappingRow {
    columnIndex: number;
    headerTitle: string;
    /** 新格式 Row 9 */
    headerTitleZh?: string;
    /** 新格式 Row 4 fs-code */
    fsCode?: string;
    /** 用户选择的 GL Category code */
    selectedGlCode: string | null;
    /** 用户选择的 GL Category 标准英文名 */
    selectedNameEn: string | null;
    /** 模糊匹配建议 */
    suggestedGlCode?: string;
    suggestedNameEn?: string;
    matchScore?: number;
    /** NAME_EN / NORMALIZED / ALIAS / ROW5 / FUZZY / UNMATCHED */
    matchSource?: string;
}

// ===================== Helpers =====================

/** 根据 matchScore 返回行背景色 */
function rowBgColor(score?: number): string | undefined {
    if (score != null && score >= 0.9) return 'success.50';
    if (score == null || score < 0.6) return 'warning.50';
    return undefined;
}

/** 根据 matchScore / matchSource 返回行左侧标签 */
function rowLabel(score?: number, matchSource?: string): string | null {
    if (matchSource === 'SOLE_OPTION') return 'Only option';
    if (matchSource === 'ALIAS') return 'Saved alias';
    if (matchSource === 'UNMATCHED' || score == null || score < 0.6) return 'Needs attention';
    if (matchSource === 'NORMALIZED') return 'Normalized';
    if (matchSource === 'ROW5') return 'From Excel';
    if (score != null && score >= 0.9) return 'Auto-matched';
    return matchSource || null;
}

function matchSourceChipColor(source?: string): 'default' | 'success' | 'warning' | 'info' {
    if (source === 'UNMATCHED' || !source) return 'warning';
    if (source === 'ALIAS' || source === 'NORMALIZED') return 'info';
    if (source === 'ROW5' || source === 'NAME_EN' || source === 'SOLE_OPTION') return 'success';
    return 'default';
}

/** 后端缺 FS / 无 Row8 标题时 headerTitle/fsCode 可能为 null */
function safeHeaderTitle(title: string | null | undefined, columnIndex: number): string {
    const t = (title ?? '').trim();
    return t || `Column ${columnIndex}`;
}

function safeOptionalText(v: string | null | undefined): string | undefined {
    if (v == null) return undefined;
    const t = String(v).trim();
    return t || undefined;
}

function normalizeMappingRow(row: MappingRow): MappingRow {
    return {
        ...row,
        headerTitle: safeHeaderTitle(row.headerTitle, row.columnIndex),
        headerTitleZh: safeOptionalText(row.headerTitleZh),
        fsCode: safeOptionalText(row.fsCode),
    };
}

/** 从全量 columnMappings 初始化；精确匹配预填；同码多列均预填（确认时后端 -DUP 消歧） */
function initRows(cols: UnmatchedColumn[]): MappingRow[] {
    return cols.map((col) => {
        const exact = shouldAutoSelectSuggestion(col.matchScore)
            || (col.glCode != null && col.matchSource != null && col.matchSource !== 'UNMATCHED');
        const suggestedGlCode = exact ? (col.suggestedGlCode || col.glCode || undefined) : undefined;
        const suggestedNameEn = exact ? col.suggestedNameEn : undefined;
        const matchScore = exact ? (col.matchScore ?? 1.0) : undefined;
        return normalizeMappingRow({
            columnIndex: col.columnIndex,
            headerTitle: safeHeaderTitle(col.headerTitle, col.columnIndex),
            headerTitleZh: safeOptionalText(col.headerTitleZh),
            fsCode: safeOptionalText(col.fsCode),
            selectedGlCode: suggestedGlCode ?? null,
            selectedNameEn: suggestedNameEn ?? null,
            suggestedGlCode,
            suggestedNameEn,
            matchScore,
            matchSource: col.matchSource,
        });
    });
}

/** Ex HKD 行与原币可共用同一叶子（确认时后端写 -EXHKD）；冲突检测用落库有效码 */
function mappingStorageGlCode(row: MappingRow): string | null {
    if (!row.selectedGlCode) return null;
    const code = row.selectedGlCode;
    const isExHkd = isExHkdColumnName(row.headerTitle);
    if (isExHkd && !/-exhkd(-dup\d+)?$/i.test(code)) {
        return `${code}-EXHKD`;
    }
    return code;
}

/** 多列映射到同一落库 GL code → 提示（确认时后端用 -DUP 消歧，不拦截 Next） */
function findDuplicateGlCodeConflicts(rows: MappingRow[]): Array<{ code: string; headers: string[] }> {
    const byCode = new Map<string, string[]>();
    for (const r of rows) {
        const storageCode = mappingStorageGlCode(r);
        if (!storageCode) continue;
        const list = byCode.get(storageCode) ?? [];
        list.push(r.headerTitle);
        byCode.set(storageCode, list);
    }
    const conflicts: Array<{ code: string; headers: string[] }> = [];
    for (const [code, headers] of byCode) {
        if (headers.length > 1) conflicts.push({ code, headers });
    }
    return conflicts;
}

type ColumnViewMode = 'needsReview' | 'all';

/** 未匹配 / 需关注：未选 GL、缺 FS、或低分（SOLE_OPTION / ALIAS 且已齐全不算） */
function isNeedsReviewRow(row: MappingRow, _duplicateCodeSet: Set<string>): boolean {
    if (!row.selectedGlCode) return true;
    // Row 4 无 fs-code：需用户确认 FS（或选 GL 后自动带出）
    if (!(row.fsCode || '').trim()) return true;
    if (row.matchSource === 'SOLE_OPTION' || row.matchSource === 'ALIAS') return false;
    if (row.matchSource === 'UNMATCHED') return true;
    if (row.matchScore == null || row.matchScore < 0.6) return true;
    return false;
}

/** 从 API 错误中提取可读信息 */
function formatMappingApiError(e: unknown): string {
    const msg = (e as any)?.message || String(e);
    if (/Duplicate entry|DuplicateKeyException|uk_gl_account_company_code/i.test(msg)) {
        return 'Could not save Co.GL aliases (duplicate key). Please retry or contact support.';
    }
    return msg || 'Mapping confirm failed';
}

type FsSelectOption = { code: string; label: string };

/** EN / ZH (code) — FS / GL 下拉共用 */
function formatEnZhCodeLabel(code: string, nameEn?: string | null, nameZh?: string | null): string {
    const en = (nameEn || '').trim();
    const zh = (nameZh || '').trim();
    if (en && zh) return `${en} / ${zh} (${code})`;
    if (en) return `${en} (${code})`;
    if (zh) return `${zh} (${code})`;
    return code;
}

function formatFsSelectLabel(code: string, nameEn?: string | null, nameZh?: string | null): string {
    return formatEnZhCodeLabel(code, nameEn, nameZh);
}

// ===================== Component =====================

export default function StepCCateMapping() {
    const { state, dispatch } = useGlInvoiceCreateCtx();

    const [rows, setRows] = useState<MappingRow[]>([]);
    const [glCategories, setGlCategories] = useState<GlCategory[]>([]);
    /** FS code → 字典（用于 FS 下拉显示 EN / ZH / code） */
    const [fsByCode, setFsByCode] = useState<Map<string, FsCategory>>(() => new Map());
    const [loading, setLoading] = useState(true);
    const [canSkip, setCanSkip] = useState(false);
    const [skipChecking, setSkipChecking] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [addGlRowIdx, setAddGlRowIdx] = useState<number | null>(null);
    /** 默认只看未匹配 / 有问题的列 */
    const [columnView, setColumnView] = useState<ColumnViewMode>('needsReview');
    /** 点击才编辑：避免每行挂 Input/Autocomplete 导致卡顿 */
    const [editingLeftIdx, setEditingLeftIdx] = useState<number | null>(null);
    const [editingGlIdx, setEditingGlIdx] = useState<number | null>(null);

    // 1. 初始化 MappingRow[]：优先从 context 恢复，否则从全量 columnMappings
    useEffect(() => {
        if (state.mappingRows && state.mappingRows.length > 0) {
            // 旧缓存可能含 null headerTitle（缺 FS 列），只修空值、不重算已选 GL
            const normalized = state.mappingRows.map(normalizeMappingRow);
            setRows(normalized);
            dispatch({ type: 'set_mapping_rows', payload: normalized });
        } else {
            const cols = state.columnMappings?.length
                ? state.columnMappings
                : state.unmatchedColumns;
            if (cols?.length) {
                const initialized = initRows(cols);
                setRows(initialized);
                dispatch({ type: 'set_mapping_rows', payload: initialized });
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [refreshingCats, setRefreshingCats] = useState(false);

    /** 始终拉取最新叶子；上传预加载可能已过期（用户刚在 GL Category 改过名称/新增） */
    const loadGlCategories = useCallback(async (opts?: { quiet?: boolean }) => {
        if (!opts?.quiet) setRefreshingCats(true);
        try {
            const [colDefs, leafCategories, fsList] = await Promise.all([
                fetchColumnDefs(),
                fetchLeafGlCategories(),
                listAllFsCategories().catch((e) => {
                    console.warn('StepCCateMapping: failed to load FS categories', e);
                    return [] as FsCategory[];
                }),
            ]);
            dispatch({ type: 'set_preloaded_column_defs', payload: colDefs });
            dispatch({ type: 'set_preloaded_gl_categories', payload: leafCategories });
            setGlCategories(leafCategories);
            const map = new Map<string, FsCategory>();
            for (const f of fsList || []) {
                const code = (f.code || '').trim();
                if (code) map.set(code, f);
            }
            setFsByCode(map);
            return leafCategories;
        } catch (e: any) {
            console.warn('StepCCateMapping: failed to load GL categories', e);
            return null;
        } finally {
            setRefreshingCats(false);
            setLoading(false);
        }
    }, [dispatch]);

    // 2. 进入本步时重新加载 GL Category（不用上传时的旧缓存）
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const cats = await loadGlCategories({ quiet: true });
            if (cancelled || !cats) return;
        })();
        return () => { cancelled = true; };
    }, [loadGlCategories]);

    // 新格式（带 fsCode）必须手选 GL，不允许 Skip 冒成 FS-as-GL
    const hasFsScopedRows = useMemo(
        () => (state.unmatchedColumns ?? []).some((c) => !!c.fsCode),
        [state.unmatchedColumns],
    );

    // 3. 检查是否可以 Skip（仅旧格式：别名二次校验）
    useEffect(() => {
        if (!state.companyId || !state.unmatchedColumns?.length || hasFsScopedRows) {
            setCanSkip(false);
            setSkipChecking(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const titles = state.unmatchedColumns!.map((c) => c.headerTitle);
                const result = await checkAliasExists(state.companyId!, titles);
                if (!cancelled) {
                    setCanSkip(result.allExist);
                }
            } catch (e: any) {
                console.warn('StepCCateMapping: checkAliasExists failed', e);
            } finally {
                if (!cancelled) setSkipChecking(false);
            }
        })();
        return () => { cancelled = true; };
    }, [state.companyId, state.unmatchedColumns, hasFsScopedRows]);

    // 去重的 GL 选项列表
    const glOptions = useMemo(() => {
        const seen = new Set<string>();
        return glCategories
            .filter((c) => c.code && !seen.has(c.code!) && (seen.add(c.code!), true))
            .sort((a, b) => (a.nameEn ?? '').localeCompare(b.nameEn ?? ''));
    }, [glCategories]);

    /**
     * FS 下拉：可挂叶子的报表行。
     * 过滤表根（FS-BS / FS-PL）、表头（isHeader）、计算行（isCalc）。
     */
    const fsCodeOptions = useMemo((): FsSelectOption[] => {
        const selectable = (code: string, cat?: FsCategory): boolean => {
            if (!code) return false;
            if (/^FS-/i.test(code)) return false; // Balance Sheet / P&L 等表根
            if (cat) {
                if (cat.isDel === 1) return false;
                if (cat.isActive === 0) return false;
                if (cat.isHeader === 1) return false; // 如 BS-NCL 非流动负债分组
                if (cat.isCalc === 1) return false;
            }
            return true;
        };

        let codes: string[];
        if (fsByCode.size > 0) {
            codes = Array.from(fsByCode.entries())
                .filter(([code, cat]) => selectable(code, cat))
                .map(([code]) => code);
        } else {
            const set = new Set<string>();
            for (const c of glOptions) {
                const fs = (c.fsCode || '').trim();
                if (fs && selectable(fs)) set.add(fs);
            }
            codes = Array.from(set);
        }
        // 当前行已有 FS（即使是旧表头码）仍保留，避免显示空白
        for (const r of rows) {
            const fs = (r.fsCode || '').trim();
            if (fs && !codes.includes(fs)) codes.push(fs);
        }
        return codes
            .sort((a, b) => a.localeCompare(b))
            .map((code) => {
                const cat = fsByCode.get(code);
                return {
                    code,
                    label: formatFsSelectLabel(code, cat?.nameEn, cat?.nameZh),
                };
            });
    }, [glOptions, fsByCode, rows]);

    /** 避免 company 别名预填与 sole-option 重复跑、覆盖用户手改 */
    const autoPrefillKeyRef = useRef<string | null>(null);

    // 先按 Co.GL 历史别名预填，再对仍空的行做「仅剩 1 个叶子」自动选
    // （上传时 companyId=0 未走后端 Step B 时，这里补上以前加过的映射）
    useEffect(() => {
        if (!glOptions.length || rows.length === 0) return;
        const prefillKey = `${state.companyId ?? 0}:${rows.map((r) => r.columnIndex).join(',')}:${glOptions.length}`;
        if (autoPrefillKeyRef.current === prefillKey) return;

        let cancelled = false;
        (async () => {
            let aliasMap = new Map<string, string>();
            if (state.companyId && state.companyId > 0) {
                try {
                    const accounts = await listAccountsByCompany(state.companyId);
                    if (cancelled) return;
                    aliasMap = buildAliasTitleToCodeMap(accounts || []);
                } catch (e) {
                    console.warn('StepCCateMapping: listAccountsByCompany failed', e);
                }
            }
            if (cancelled) return;

            const glByCode = new Map(
                glOptions
                    .filter((c): c is GlCategory & { code: string } => !!c.code)
                    .map((c) => [c.code, c]),
            );

            setRows((prev) => {
                if (!prev.length) return prev;
                const { rows: afterAlias } = applyCompanyAliasToMappingRows(prev, aliasMap, glByCode);
                let changed = afterAlias !== prev
                    && afterAlias.some((r, i) => r.selectedGlCode !== prev[i]?.selectedGlCode);

                const next: MappingRow[] = afterAlias.map((row) => {
                    if (row.selectedGlCode) {
                        // 已选 GL 但缺 FS：从叶子补 FS（别名预填后常见）
                        if (!(row.fsCode || '').trim()) {
                            const fs = (glByCode.get(row.selectedGlCode)?.fsCode || '').trim();
                            if (fs) {
                                changed = true;
                                return { ...row, fsCode: fs };
                            }
                        }
                        return row;
                    }
                    const opts = filterGlCategoriesForHeader(glOptions, row.headerTitle, row.fsCode);
                    const code = opts[0]?.code;
                    if (opts.length !== 1 || !code) return row;
                    changed = true;
                    const only = opts[0];
                    const isExHkd = isExHkdColumnName(row.headerTitle);
                    const nameEn = only.nameEn
                        ? (isExHkd && !isExHkdColumnName(only.nameEn)
                            ? `${only.nameEn} (Ex HKD)`
                            : only.nameEn)
                        : null;
                    return {
                        ...row,
                        fsCode: (row.fsCode || '').trim() || (only.fsCode || '').trim() || undefined,
                        selectedGlCode: code,
                        selectedNameEn: nameEn,
                        suggestedGlCode: code,
                        suggestedNameEn: nameEn ?? undefined,
                        matchScore: 1.0,
                        matchSource: row.matchSource === 'UNMATCHED' ? 'SOLE_OPTION' : (row.matchSource || 'SOLE_OPTION'),
                    };
                });

                autoPrefillKeyRef.current = prefillKey;
                if (!changed) return prev;
                dispatch({ type: 'set_mapping_rows', payload: next });
                return next;
            });
        })();

        return () => { cancelled = true; };
    }, [glOptions, rows.length, state.companyId, dispatch]);

    // 已选 GL、仍缺 FS 的行（如本次会话已 ALIAS 过）：用叶子 FS 回填，避免一直停在 Needs review
    useEffect(() => {
        if (!glOptions.length || rows.length === 0) return;
        const needBackfill = rows.some(
            (r) => r.selectedGlCode && !(r.fsCode || '').trim(),
        );
        if (!needBackfill) return;
        const glByCode = new Map(
            glOptions
                .filter((c): c is GlCategory & { code: string } => !!c.code)
                .map((c) => [c.code, c]),
        );
        setRows((prev) => {
            let changed = false;
            const next = prev.map((row) => {
                if (!row.selectedGlCode || (row.fsCode || '').trim()) return row;
                const fs = (glByCode.get(row.selectedGlCode)?.fsCode || '').trim();
                if (!fs) return row;
                changed = true;
                return { ...row, fsCode: fs };
            });
            if (!changed) return prev;
            dispatch({ type: 'set_mapping_rows', payload: next });
            return next;
        });
    }, [glOptions, rows, dispatch]);

    const allMapped = rows.length > 0 && rows.every((r) => r.selectedGlCode);
    const mappedCount = rows.filter((r) => r.selectedGlCode).length;

    const rowGlOptions = useCallback((headerTitle: string, suggestedGlCode?: string, fsCode?: string) => {
        const filtered = filterGlCategoriesForHeader(glOptions, headerTitle, fsCode);
        return sortGlOptionsWithSuggestion(filtered, suggestedGlCode);
    }, [glOptions]);

    const duplicateConflicts = useMemo(() => findDuplicateGlCodeConflicts(rows), [rows]);

    const duplicateConflictMessage = useMemo(() => {
        if (duplicateConflicts.length === 0) return null;
        const detail = duplicateConflicts
            .map((c) => `${c.code}: ${c.headers.join(', ')}`)
            .join('; ');
        return `Same GL on multiple columns (will keep separate via -DUP): ${detail}. You can Add GL if you want distinct leaves.`;
    }, [duplicateConflicts]);

    const duplicateCodeSet = useMemo(
        () => new Set(duplicateConflicts.map((c) => c.code)),
        [duplicateConflicts],
    );

    const needsReviewCount = useMemo(
        () => rows.filter((r) => isNeedsReviewRow(r, duplicateCodeSet)).length,
        [rows, duplicateCodeSet],
    );

    /** 保留原始 idx，避免过滤后 handleSelect / Add GL 写错行 */
    const visibleRows = useMemo(() => {
        const indexed = rows.map((row, idx) => ({ row, idx }));
        if (columnView === 'all') return indexed;
        return indexed.filter(({ row }) => isNeedsReviewRow(row, duplicateCodeSet));
    }, [rows, columnView, duplicateCodeSet]);

    const existingCodeSet = useMemo(
        () => new Set(glCategories.map((c) => c.code!).filter(Boolean)),
        [glCategories],
    );

    const addGlPrefill: AddGlCategoryPrefill | null = useMemo(() => {
        if (addGlRowIdx == null) return null;
        const row = rows[addGlRowIdx];
        if (!row) return null;
        const underFs = row.fsCode
            ? glOptions.filter((c) => (c.fsCode || '').trim() === (row.fsCode || '').trim())
            : [];
        const template = (row.selectedGlCode
            ? glOptions.find((c) => c.code === row.selectedGlCode)
            : null)
            ?? (row.suggestedGlCode
                ? glOptions.find((c) => c.code === row.suggestedGlCode)
                : null)
            ?? underFs[0]
            ?? null;
        return {
            nameEn: row.headerTitle,
            nameZh: row.headerTitleZh,
            fsCode: row.fsCode || template?.fsCode,
            template,
        };
    }, [addGlRowIdx, rows, glOptions]);

    const handleSelect = useCallback((idx: number, glCode: string | null, nameEn: string | null) => {
        setRows((prev) => {
            const updated = prev.map((r, i) => {
                if (i !== idx) return r;
                const cat = glCode
                    ? (glOptions.find((c) => c.code === glCode)
                        ?? glCategories.find((c) => c.code === glCode))
                    : undefined;
                const fsFromGl = (cat?.fsCode || '').trim();
                const keepFs = (r.fsCode || '').trim();
                return {
                    ...r,
                    selectedGlCode: glCode,
                    selectedNameEn: nameEn,
                    // 左侧尚无 FS 时，用所选叶子 FS 补全（缺 FS 会进 Needs review）
                    fsCode: keepFs || fsFromGl || undefined,
                };
            });
            dispatch({ type: 'set_mapping_rows', payload: updated });
            return updated;
        });
        setError(null);
        dispatch({ type: 'set_step_error', payload: null });
    }, [dispatch, glOptions, glCategories]);

    /** 左侧 Excel 列名 EN / ZH / FS 可改；与所选 GL 标准名不同时，确认映射会写入 Co.GL alias */
    const handleHeaderFieldChange = useCallback((
        idx: number,
        patch: Partial<Pick<MappingRow, 'headerTitle' | 'headerTitleZh' | 'fsCode'>>,
    ) => {
        setRows((prev) => {
            const updated = prev.map((r, i) => (i === idx ? { ...r, ...patch } : r));
            dispatch({ type: 'set_mapping_rows', payload: updated });
            return updated;
        });
        setError(null);
    }, [dispatch]);

    const handleGlCreated = useCallback((created: GlCategory, rowIdx: number) => {
        setGlCategories((prev) => {
            if (created.code && prev.some((c) => c.code === created.code)) return prev;
            return [...prev, created];
        });
        if (state.preloadedGlCategories) {
            const next = [...(state.preloadedGlCategories as GlCategory[])];
            if (created.code && !next.some((c) => c.code === created.code)) {
                next.push(created);
                dispatch({ type: 'set_preloaded_gl_categories', payload: next });
            }
        }
        handleSelect(rowIdx, created.code ?? null, created.nameEn ?? null);
        setAddGlRowIdx(null);
    }, [dispatch, handleSelect, state.preloadedGlCategories]);

    // 预加载逻辑（使用共享工厂函数，映射确认后或 fallback 时触发）
    const startPreload = useMemo(() => createStartPreload(dispatch), [dispatch]);

    // beforeNext: 调用 confirmLegacyMapping → 重新预加载后端已映射数据 + 别名列定义
    const handleConfirmAndNext = useCallback(async (): Promise<boolean> => {
        if (!state.importToken) return false;
        if (!allMapped) return false;
        // 同 GL 多列允许：后端 confirm 用 -DUP 消歧，不在此拦截
        setSubmitting(true);
        setError(null);
        dispatch({ type: 'set_step_error', payload: null });
        try {
            const apiMappings = rows.map((m) => ({
                columnIndex: m.columnIndex,
                headerTitle: m.headerTitle,
                code: m.selectedGlCode!,
                selectedNameEn: m.selectedNameEn ?? undefined,
                aliasZh: m.headerTitleZh?.trim() || undefined,
            }));
            const result = await confirmLegacyMapping({
                importToken: state.importToken,
                companyId: state.companyId ?? 0,
                mappings: apiMappings,
            });
            if (result.errors?.length) {
                throw new Error(result.errors.join('; '));
            }
            // 注意：不清除 unmatchedColumns，保持 4 步布局（activeStep 3 → InvoiceEntry）
            dispatch({ type: 'set_mapping_rows', payload: null });

            const previewList = result.previewData?.list ?? [];
            const totalRows = result.totalRows ?? previewList.length ?? state.preloadedGlData?.rows.length ?? 0;
            const rowsWithAmount = previewList.length > 0
                ? countGlSheetRowsWithAmount(previewList)
                : (state.preloadedGlData?.rows.filter((r) =>
                    r.drCrItems.some((d) => d.amount && parseFloat(d.amount) !== 0),
                ).length ?? 0);
            dispatch({
                type: 'set_mapping_summary',
                payload: buildMappingConfirmSummary(rows, totalRows, rowsWithAmount),
            });

            if (result.keyMapping && state.preloadedGlData) {
                // ★ 后端提供权威 keyMapping → 客户端快速重映射（避免二次网络请求）
                const remapped = remapPreloadedData(state.preloadedGlData, result.keyMapping);
                dispatch({ type: 'set_preloaded_gl_data', payload: remapped });
                // 上年汇总必须写成正式 fs|gl key，否则 Last Year 全 0
                const priorFromPreview = result.previewData?.summaryPriorYear;
                dispatch({
                    type: 'set_summary_prior_year',
                    payload: (priorFromPreview && Object.keys(priorFromPreview).length > 0)
                        ? priorFromPreview
                        : remapped.summaryPriorYear,
                });
                // columnMetaMap 也要从 _UNM_ 换成正式 key，否则保存后金额列全丢
                const metaFromPreview = result.previewData?.columnMetaMap;
                const remappedMeta = (metaFromPreview && Object.keys(metaFromPreview).length > 0)
                    ? metaFromPreview
                    : remapColumnMetaMap(state.preloadedColumnMetaMap, result.keyMapping);
                if (remappedMeta && Object.keys(remappedMeta).length > 0) {
                    dispatch({ type: 'set_preloaded_column_meta_map', payload: remappedMeta });
                }
                // 同步更新列定义：后端可能返回包含别名信息的更新列定义
                if (result.previewData?.columnDefs?.length) {
                    dispatch({ type: 'set_preloaded_column_defs', payload: result.previewData.columnDefs });
                }
                dispatch({ type: 'set_pending_legacy_mapping', payload: false });
                dispatch({ type: 'set_preload_status', payload: 'done' });
            } else {
                // 后端未提供 keyMapping → 必须重新从后端获取已映射数据 + 别名列定义
                dispatch({ type: 'set_pending_legacy_mapping', payload: false });
                // 清掉占位 prior，让 startPreload 写入正式 key
                dispatch({ type: 'set_summary_prior_year', payload: {} });
                dispatch({ type: 'set_preloaded_column_meta_map', payload: null });
                startPreload(state.importToken, state.companyId);
            }
            return true;
        } catch (e: any) {
            const msg = formatMappingApiError(e);
            setError(msg);
            dispatch({ type: 'set_step_error', payload: msg });
            dispatch({ type: 'set_pending_legacy_mapping', payload: false });
            dispatch({ type: 'set_preload_status', payload: 'error' });
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [state.importToken, state.companyId, state.preloadedGlData, state.preloadedColumnMetaMap, allMapped, rows, dispatch, startPreload]);

    // Skip: 调用 confirmLegacyMapping({ autoResolveFromAlias: true })
    const handleSkip = useCallback(async () => {
        if (!state.importToken) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await confirmLegacyMapping({
                importToken: state.importToken,
                companyId: state.companyId ?? 0,
                mappings: [],
                autoResolveFromAlias: true,
            });
            dispatch({ type: 'set_unmatched_columns', payload: null });
            dispatch({ type: 'set_column_mappings', payload: null });
            dispatch({ type: 'set_mapping_rows', payload: null });

            if (result.keyMapping && state.preloadedGlData) {
                // 后端提供 keyMapping → 客户端快速重映射
                const remapped = remapPreloadedData(state.preloadedGlData, result.keyMapping);
                dispatch({ type: 'set_preloaded_gl_data', payload: remapped });
                const priorFromPreview = result.previewData?.summaryPriorYear;
                dispatch({
                    type: 'set_summary_prior_year',
                    payload: (priorFromPreview && Object.keys(priorFromPreview).length > 0)
                        ? priorFromPreview
                        : remapped.summaryPriorYear,
                });
                const metaFromPreview = result.previewData?.columnMetaMap;
                const remappedMeta = (metaFromPreview && Object.keys(metaFromPreview).length > 0)
                    ? metaFromPreview
                    : remapColumnMetaMap(state.preloadedColumnMetaMap, result.keyMapping);
                if (remappedMeta && Object.keys(remappedMeta).length > 0) {
                    dispatch({ type: 'set_preloaded_column_meta_map', payload: remappedMeta });
                }
                if (result.previewData?.columnDefs?.length) {
                    dispatch({ type: 'set_preloaded_column_defs', payload: result.previewData.columnDefs });
                }
                dispatch({ type: 'set_pending_legacy_mapping', payload: false });
                dispatch({ type: 'set_preload_status', payload: 'done' });
            } else {
                // 重新从后端获取已映射数据 + 别名列定义
                dispatch({ type: 'set_pending_legacy_mapping', payload: false });
                dispatch({ type: 'set_summary_prior_year', payload: {} });
                dispatch({ type: 'set_preloaded_column_meta_map', payload: null });
                startPreload(state.importToken, state.companyId);
            }
        } catch (e: any) {
            setError(e?.message || 'Auto-resolve failed');
            dispatch({ type: 'set_pending_legacy_mapping', payload: false });
            dispatch({ type: 'set_preload_status', payload: 'error' });
        } finally {
            setSubmitting(false);
        }
    }, [state.importToken, state.companyId, state.preloadedGlData, state.preloadedColumnMetaMap, dispatch, startPreload]);

    // 注册 beforeNext
    useEffect(() => {
        dispatch({ type: 'set_before_next', payload: handleConfirmAndNext });
        return () => { dispatch({ type: 'set_before_next', payload: undefined }); };
    }, [handleConfirmAndNext, dispatch]);

    // ===================== Render =====================

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress size={28} />
                <Typography sx={{ ml: 1.5 }} variant="body2" color="text.secondary">Loading GL categories...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>Column Mapping</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Review every amount column → GL mapping. Click left fields or GL to edit; when EN / ZH differ from the selected GL Category, they are saved as Co.GL aliases on Next.
                        {hasFsScopedRows ? ' Ex HKD may reuse the same leaf as its FX original.' : ''}
                    </Typography>
                </Box>
                {canSkip && !skipChecking && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SkipNextIcon />}
                        disabled={submitting}
                        onClick={handleSkip}
                        sx={{ whiteSpace: 'nowrap', ml: 2 }}
                    >
                        Skip Mapping
                    </Button>
                )}
                {skipChecking && (
                    <CircularProgress size={18} sx={{ ml: 2 }} />
                )}
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>
            )}

            {duplicateConflictMessage && !error && (
                <Alert severity="warning" sx={{ mb: 1 }}>{duplicateConflictMessage}</Alert>
            )}

            {rows.length > 0 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                    sx={{ mb: 1 }}
                >
                    <Typography variant="caption" color="text.secondary">
                        Mapped {mappedCount} / {rows.length} columns
                        {!allMapped && ' — map all columns before continuing'}
                        {columnView === 'needsReview' && needsReviewCount === 0 && allMapped && !duplicateConflictMessage
                            ? ' — all clear; switch to All to review auto-matched columns'
                            : null}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Tooltip title="Reload GL categories (after editing in GL Category)">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={refreshingCats || submitting}
                                    onClick={() => loadGlCategories()}
                                    aria-label="Refresh GL categories"
                                >
                                    {refreshingCats
                                        ? <CircularProgress size={16} />
                                        : <RefreshIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={columnView}
                            onChange={(_, next: ColumnViewMode | null) => {
                                if (next) setColumnView(next);
                            }}
                            aria-label="Column list filter"
                        >
                            <ToggleButton value="needsReview" sx={{ textTransform: 'none', px: 1.25 }}>
                                Needs review ({needsReviewCount})
                            </ToggleButton>
                            <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25 }}>
                                All ({rows.length})
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                </Stack>
            )}

            {/* Mapping rows — scrollable */}
            <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
                {columnView === 'needsReview' && rows.length > 0 && visibleRows.length === 0 ? (
                    <Alert severity="success" sx={{ mb: 1 }}>
                        No unmatched or conflicting columns. Switch to All if you want to review auto-matched rows.
                    </Alert>
                ) : null}
                <Stack spacing={1}>
                    {visibleRows.map(({ row, idx }) => {
                        const bg = rowBgColor(row.matchScore);
                        const label = rowLabel(row.matchScore, row.matchSource);
                        const storageCode = mappingStorageGlCode(row);
                        const isDuplicateCode = !!(
                            (storageCode && duplicateCodeSet.has(storageCode))
                            || (row.selectedGlCode && duplicateCodeSet.has(row.selectedGlCode))
                        );
                        const optionsForRow = rowGlOptions(row.headerTitle, row.suggestedGlCode, row.fsCode);
                        const chipClickable = canApplySuggestionChip(row.matchScore);
                        const selectedCat = row.selectedGlCode
                            ? (glOptions.find((c) => c.code === row.selectedGlCode)
                                ?? glCategories.find((c) => c.code === row.selectedGlCode))
                            : undefined;
                        const stdNameEn = (selectedCat?.nameEn || row.selectedNameEn || '').trim();
                        const stdNameZh = (selectedCat?.nameZh || '').trim();
                        const stdFs = (selectedCat?.fsCode || '').trim();
                        const rowTitleEn = (row.headerTitle || '').trim();
                        const rowTitleZh = (row.headerTitleZh || '').trim();
                        const rowFs = (row.fsCode || '').trim();
                        const enDiffers = !!row.selectedGlCode
                            && !!rowTitleEn
                            && !!stdNameEn
                            && rowTitleEn.toLowerCase() !== stdNameEn.toLowerCase();
                        const zhDiffers = !!row.selectedGlCode
                            && !!rowTitleZh
                            && (!!stdNameZh
                                ? rowTitleZh !== stdNameZh
                                : true);
                        const fsDiffers = !!row.selectedGlCode
                            && !!rowFs
                            && !!stdFs
                            && rowFs !== stdFs;
                        const willSaveAlias = enDiffers || zhDiffers;
                        const leftEditing = editingLeftIdx === idx;
                        const glEditing = editingGlIdx === idx;
                        const fsDisplayLabel = row.fsCode
                            ? formatFsSelectLabel(
                                row.fsCode,
                                fsByCode.get(row.fsCode)?.nameEn,
                                fsByCode.get(row.fsCode)?.nameZh,
                            )
                            : '';
                        const glDisplayLabel = selectedCat
                            ? formatEnZhCodeLabel(
                                selectedCat.code || '',
                                selectedCat.nameEn,
                                selectedCat.nameZh,
                            )
                            : (row.selectedGlCode
                                ? formatEnZhCodeLabel(row.selectedGlCode, row.selectedNameEn, null)
                                : '');
                        return (
                            <Stack
                                key={row.columnIndex}
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{
                                    px: 1.5, py: 0.75,
                                    borderRadius: 1,
                                    ...(isDuplicateCode ? { bgcolor: 'error.50', outline: '1px solid', outlineColor: 'error.light' }
                                        : bg ? { bgcolor: bg } : {}),
                                }}
                            >
                                {/* 默认只读；点击后才挂 Input / FS Autocomplete */}
                                <Box sx={{ flex: '1.45 1 380px', minWidth: 360, maxWidth: 560 }}>
                                    {leftEditing ? (
                                        <Stack spacing={0.5}>
                                            <InputField
                                                size="small"
                                                fullWidth
                                                compact
                                                align="left"
                                                label="EN"
                                                autoFocus
                                                value={row.headerTitle}
                                                onChange={(e) => handleHeaderFieldChange(idx, { headerTitle: e.target.value })}
                                            />
                                            <InputField
                                                size="small"
                                                fullWidth
                                                compact
                                                align="left"
                                                label="ZH"
                                                value={row.headerTitleZh ?? ''}
                                                onChange={(e) => handleHeaderFieldChange(idx, {
                                                    headerTitleZh: e.target.value,
                                                })}
                                            />
                                            <Autocomplete
                                                size="small"
                                                fullWidth
                                                options={fsCodeOptions}
                                                value={
                                                    row.fsCode
                                                        ? (fsCodeOptions.find((o) => o.code === row.fsCode)
                                                            ?? {
                                                                code: row.fsCode,
                                                                label: fsDisplayLabel || row.fsCode,
                                                            })
                                                        : null
                                                }
                                                onChange={(_, val) => handleHeaderFieldChange(idx, {
                                                    fsCode: val?.code?.trim() || undefined,
                                                })}
                                                getOptionLabel={(o) => o.label}
                                                isOptionEqualToValue={(a, b) => a.code === b.code}
                                                componentsProps={{
                                                    paper: { sx: { minWidth: 420, maxWidth: 560 } },
                                                }}
                                                renderInput={(params) => (
                                                    <InputField
                                                        {...params}
                                                        label="FS"
                                                        placeholder="EN / ZH (code)"
                                                        align="left"
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            title: fsDisplayLabel || undefined,
                                                        }}
                                                    />
                                                )}
                                            />
                                            <Stack direction="row" justifyContent="flex-end">
                                                <Button
                                                    size="small"
                                                    onClick={() => setEditingLeftIdx(null)}
                                                    sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
                                                >
                                                    Done
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    ) : (
                                        <Tooltip title="Click to edit EN / ZH / FS">
                                            <Box
                                                onClick={() => {
                                                    setEditingLeftIdx(idx);
                                                    setEditingGlIdx(null);
                                                }}
                                                sx={{
                                                    cursor: 'pointer',
                                                    borderRadius: 1,
                                                    px: 0.75,
                                                    py: 0.5,
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight={600} noWrap title={row.headerTitle}>
                                                    {row.headerTitle || '—'}
                                                </Typography>
                                                {row.headerTitleZh ? (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        noWrap
                                                        display="block"
                                                        title={row.headerTitleZh}
                                                    >
                                                        {row.headerTitleZh}
                                                    </Typography>
                                                ) : null}
                                                {row.fsCode ? (
                                                    <Typography
                                                        variant="caption"
                                                        color="info.main"
                                                        noWrap
                                                        display="block"
                                                        title={fsDisplayLabel}
                                                    >
                                                        {fsDisplayLabel}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled" display="block">
                                                        FS: —
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Tooltip>
                                    )}
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                        {willSaveAlias ? (
                                            <Chip
                                                size="small"
                                                label="Save as Co.GL alias"
                                                color="info"
                                                variant="outlined"
                                                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                                            />
                                        ) : null}
                                        {fsDiffers ? (
                                            <Tooltip title={`Selected GL FS is ${stdFs}; left FS only filters the dropdown. Column key uses GL Category FS.`}>
                                                <Chip
                                                    size="small"
                                                    label="FS ≠ GL"
                                                    color="warning"
                                                    variant="outlined"
                                                    sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                                                />
                                            </Tooltip>
                                        ) : null}
                                        {row.matchSource ? (
                                            <Chip
                                                size="small"
                                                label={row.matchSource}
                                                color={matchSourceChipColor(row.matchSource)}
                                                variant="outlined"
                                                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                                            />
                                        ) : null}
                                        {label ? (
                                            <Typography variant="caption" color={bg === 'success.50' ? 'success.main' : 'warning.main'}>
                                                {label}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                    {isDuplicateCode && (
                                        <Typography variant="caption" color="info.main" display="block">
                                            Shared GL — will save as separate -DUP column
                                        </Typography>
                                    )}
                                </Box>

                                <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>→</Typography>

                                {/* GL：默认只读文案；点击后才挂 Autocomplete */}
                                {glEditing ? (
                                    <Autocomplete
                                        size="small"
                                        openOnFocus
                                        sx={{
                                            flex: '0.75 1 200px',
                                            minWidth: 180,
                                            maxWidth: 320,
                                            '& .MuiInputBase-input': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            },
                                        }}
                                        options={optionsForRow}
                                        getOptionLabel={(o) => formatEnZhCodeLabel(
                                            o.code || '',
                                            o.nameEn,
                                            o.nameZh,
                                        )}
                                        value={optionsForRow.find((o) => o.code === row.selectedGlCode)
                                            ?? glOptions.find((o) => o.code === row.selectedGlCode)
                                            ?? null}
                                        onChange={(_, val) => {
                                            handleSelect(idx, val?.code ?? null, val?.nameEn ?? null);
                                            setEditingGlIdx(null);
                                        }}
                                        onClose={() => setEditingGlIdx(null)}
                                        renderOption={(props, o) => {
                                            const en = (o.nameEn || '').trim();
                                            const zh = (o.nameZh || '').trim();
                                            return (
                                                <li {...props} key={o.code}>
                                                    <Box sx={{ py: 0.25, minWidth: 0, width: '100%' }}>
                                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                            {en || o.code}
                                                            {zh ? (
                                                                <Typography component="span" variant="body2" color="text.secondary">
                                                                    {` / ${zh}`}
                                                                </Typography>
                                                            ) : null}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {o.code}
                                                        </Typography>
                                                    </Box>
                                                </li>
                                            );
                                        }}
                                        ListboxProps={{ style: { maxHeight: 320 } }}
                                        componentsProps={{
                                            paper: { sx: { minWidth: 420, maxWidth: 560 } },
                                        }}
                                        renderInput={(params) => (
                                            <InputField
                                                {...params}
                                                placeholder="EN / ZH (code)"
                                                align="left"
                                                autoFocus
                                                inputProps={{
                                                    ...params.inputProps,
                                                    title: glDisplayLabel || undefined,
                                                }}
                                            />
                                        )}
                                        isOptionEqualToValue={(opt, val) => opt.code === val.code}
                                    />
                                ) : (
                                    <Tooltip title={glDisplayLabel || 'Click to select GL Category'}>
                                        <Box
                                            onClick={() => {
                                                setEditingGlIdx(idx);
                                                setEditingLeftIdx(null);
                                            }}
                                            sx={{
                                                flex: '0.75 1 200px',
                                                minWidth: 180,
                                                maxWidth: 320,
                                                cursor: 'pointer',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.75,
                                                minHeight: 40,
                                                display: 'flex',
                                                alignItems: 'center',
                                                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.light' },
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                noWrap
                                                color={glDisplayLabel ? 'text.primary' : 'text.secondary'}
                                                sx={{ width: '100%' }}
                                            >
                                                {glDisplayLabel || 'Select GL Category'}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                )}

                                <Tooltip title="Create a new GL leaf for this column (preferred over reusing the same code)">
                                    <Button
                                        size="small"
                                        variant={isDuplicateCode ? 'contained' : 'outlined'}
                                        color={isDuplicateCode ? 'warning' : 'primary'}
                                        startIcon={<AddIcon />}
                                        onClick={() => setAddGlRowIdx(idx)}
                                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                    >
                                        Add GL
                                    </Button>
                                </Tooltip>

                                {/* 模糊建议提示 */}
                                {row.suggestedGlCode && (
                                    <Tooltip
                                        title={chipClickable
                                            ? `Suggested: ${row.suggestedNameEn} (score: ${((row.matchScore ?? 0) * 100).toFixed(0)}%) — click to apply`
                                            : `Weak match (${((row.matchScore ?? 0) * 100).toFixed(0)}%) — please select manually`}
                                    >
                                        <Chip
                                            label={`${((row.matchScore ?? 0) * 100).toFixed(0)}%`}
                                            size="small"
                                            color={
                                                (row.matchScore ?? 0) >= 0.9 ? 'success'
                                                    : (row.matchScore ?? 0) >= 0.6 ? 'info'
                                                        : 'warning'
                                            }
                                            variant="outlined"
                                            onClick={chipClickable
                                                ? () => handleSelect(idx, row.suggestedGlCode!, row.suggestedNameEn ?? null)
                                                : undefined}
                                            sx={{ cursor: chipClickable ? 'pointer' : 'default' }}
                                        />
                                    </Tooltip>
                                )}
                            </Stack>
                        );
                    })}
                </Stack>
            </Box>

            {submitting && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={18} />
                    <Typography sx={{ ml: 1 }} variant="body2" color="text.secondary">Confirming mappings...</Typography>
                </Box>
            )}

            <MappingAddGlCategoryDialog
                open={addGlRowIdx != null}
                prefill={addGlPrefill}
                existingCodes={existingCodeSet}
                onClose={() => setAddGlRowIdx(null)}
                onCreated={(created) => {
                    if (addGlRowIdx != null) handleGlCreated(created, addGlRowIdx);
                }}
            />
        </Box>
    );
}
